/**
 * AgentTrendChart.jsx
 * Jahresverlauf: Agent vs. Team — wöchentlich (oder monatlich als Fallback).
 * Tabs: Abgeschlossen | Bearbeitungszeit | FCR (nur wenn Daten vorhanden)
 * Bezier-Kurven ohne Punkte, leichte Füllfläche unter der Agenten-Linie.
 */
import React, { useState, useRef, useCallback } from 'react';

// viewBox deutlich breiter als die alte 640er-Variante: das Chart läuft jetzt
// über die volle Fensterbreite, mit der kleinen viewBox wirkten Linien/Text/
// Tooltip dadurch riesig (jede viewBox-Einheit = viele reale Pixel). Mit
// W=1200 bleibt die Schrift bei gleicher CSS-Fontgrösse kompakt.
const PL = 46, PR = 20, PT = 18, PB = 32;

function catmullRom(pts) {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

// Zerlegt eine Werte-Reihe in zusammenhaengende Segmente: bei null-Luecken
// (fehlende Wochen, z.B. Grenzwoche ohne Daten) wird die Kurve unterbrochen
// statt glatt darueber zu interpolieren — das Catmull-Rom-Spline schoss sonst
// durch die Luecke und erzeugte falsche Ausreisser/Dips im Verlauf.
function toSegments(vals, toX, toY) {
  const segments = [];
  let cur = [];
  vals.forEach((v, i) => {
    if (v != null) {
      cur.push({ x: toX(i), y: toY(v), v });
    } else if (cur.length > 0) {
      segments.push(cur);
      cur = [];
    }
  });
  if (cur.length > 0) segments.push(cur);
  return segments;
}

// Verbindungslinien zwischen zwei Datensegmenten, die durch eine Luecke
// (Ferien/Abwesenheit -> kein Wert) getrennt sind — geradlinig, nicht per
// Spline (wir haben keine echten Zwischenwerte, ein Bezier waere erfunden).
// Wird separat und gestrichelt gezeichnet, damit klar bleibt "hier fehlen
// Daten wegen Abwesenheit", statt eine harte, verwirrende Luecke zu zeigen.
function gapBridges(segments) {
  const bridges = [];
  for (let i = 0; i < segments.length - 1; i++) {
    bridges.push([segments[i][segments[i].length - 1], segments[i + 1][0]]);
  }
  return bridges;
}

function Chart({ datenpunkte, agentKey, teamKey, color, formatY, formatTip, getZielwert, clampMax }) {
  const W = 1200, H = 200;
  const cW = W - PL - PR, cH = H - PT - PB;
  const n  = datenpunkte.length;
  const svgRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);

  const handleMove = useCallback((e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const xSvg = ((e.clientX - rect.left) / rect.width) * W;
    const i = Math.round(((xSvg - PL) / cW) * (n - 1));
    setHoverIdx(Math.max(0, Math.min(n - 1, i)));
  }, [n, cW]);
  const handleLeave = useCallback(() => setHoverIdx(null), []);

  if (n < 2) return <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-tertiary,#aaa)', padding: '40px 0' }}>Nicht genug Daten</div>;

  const agentVals = datenpunkte.map(p => p.agent[agentKey]);
  const teamVals  = datenpunkte.map(p => p.team[teamKey]);
  const zielVals  = getZielwert ? datenpunkte.map(getZielwert) : [];
  const allVals   = [...agentVals, ...teamVals, ...zielVals].filter(v => v != null && v > 0);

  if (allVals.length === 0) return <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-tertiary,#aaa)', padding: '40px 0' }}>Keine Daten</div>;

  const minV = Math.min(...allVals), maxV = Math.max(...allVals);
  const pad  = (maxV - minV) * 0.18 || maxV * 0.1 || 1;
  const lo   = Math.max(0, minV - pad);
  // Prozent-Metriken (FCR) bei 100% deckeln — die Achse lief sonst bis 109%.
  const hi   = clampMax != null ? Math.min(maxV + pad, clampMax) : maxV + pad;

  const toX = i => PL + (i / (n - 1)) * cW;
  const toY = v => PT + (1 - (v - lo) / (hi - lo)) * cH;

  // Segmente statt durchgehender Punktlisten: Luecken (null-Wochen)
  // unterbrechen die Kurve, siehe toSegments.
  const agentSegs = toSegments(agentVals, toX, toY);
  const teamSegs  = toSegments(teamVals, toX, toY);
  const zielSegs  = toSegments(zielVals, toX, toY);
  const agentGaps = gapBridges(agentSegs);

  const yTicks = [lo, (lo + hi) / 2, hi];
  const baseY  = PT + cH;

  const fillPaths = agentSegs
    .filter(seg => seg.length > 1)
    .map(seg => `${catmullRom(seg)} L ${seg[seg.length - 1].x} ${baseY} L ${seg[0].x} ${baseY} Z`);

  const hp = hoverIdx != null ? datenpunkte[hoverIdx] : null;
  // timeZone UTC: die Wochen-Grenzen kommen als UTC-Mitternacht (Mo 00:00 UTC
  // bis So 23:59 UTC). Ohne UTC-Anzeige rutscht das Wochenende in Zurich
  // (UTC+1/+2) auf Montag 01:59 → Tooltip zeigte "06.07.–13.07." statt –12.07.
  const fmtDate = iso => new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });

  return (
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block', overflow: 'visible', cursor: 'crosshair' }}
      onMouseMove={handleMove} onMouseLeave={handleLeave}>
      {yTicks.map((v, i) => {
        const y = toY(v);
        return (
          <g key={i}>
            <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="var(--border,#e5e7eb)" strokeWidth={0.8} />
            <text x={PL - 5} y={y + 4} textAnchor="end" fontSize={9} fill="var(--text-tertiary,#9ca3af)">{formatY(v)}</text>
          </g>
        );
      })}

      {datenpunkte.map((p, i) => {
        if (!p.label) return null;
        return (
          <text key={i} x={toX(i)} y={H - 6} textAnchor="middle" fontSize={10}
            fill={p.isCurrent ? color : 'var(--text-secondary,#6b7280)'}
            fontWeight={p.isCurrent ? 600 : 400}>
            {p.label || p.labelFull}
          </text>
        );
      })}

      {fillPaths.map((d, i) => (
        <path key={`fill-${i}`} d={d} fill={color} fillOpacity={0.07} />
      ))}
      {/* Team-Schnitt bewusst dezent — Hintergrund-Kontext, kein Hauptvergleich
          (siehe Diskussion: instabile Stichprobengroesse je Woche). */}
      {teamSegs.filter(s => s.length > 1).map((seg, i) => (
        <path key={`team-${i}`} d={catmullRom(seg)} fill="none"
          stroke="var(--text-tertiary,#9ca3af)" strokeWidth={1} strokeOpacity={0.45}
          strokeDasharray="4 4" strokeLinecap="round" strokeLinejoin="round" />
      ))}
      {/* Zielwert — feste Linie, unabhaengig von Team-Besetzung. Deutlich
          sichtbarer als der Team-Schnitt, aber dünner als die eigene Kurve. */}
      {zielSegs.filter(s => s.length > 1).map((seg, i) => (
        <path key={`ziel-${i}`} d={catmullRom(seg)} fill="none"
          stroke="#c08a1e" strokeWidth={1.5}
          strokeDasharray="1 4" strokeLinecap="round" strokeLinejoin="round" />
      ))}
      {/* Luecken-Brücken zuerst (unter der echten Linie): gestrichelt, Amber
          wie die Abwesenheits-Markierung — signalisiert "keine Daten wegen
          Abwesenheit", keine erfundene Trendfortsetzung. */}
      {agentGaps.map((seg, i) => (
        <line key={`agent-gap-${i}`} x1={seg[0].x} y1={seg[0].y} x2={seg[1].x} y2={seg[1].y}
          stroke="#f59e0b" strokeWidth={1.75} strokeOpacity={0.55}
          strokeDasharray="5 4" strokeLinecap="round" />
      ))}
      {agentSegs.filter(s => s.length > 1).map((seg, i) => (
        <path key={`agent-${i}`} d={catmullRom(seg)} fill="none"
          stroke={color} strokeWidth={2.5}
          strokeLinecap="round" strokeLinejoin="round" />
      ))}
      {/* Einzelne isolierte Punkte (Nachbarwochen fehlen) sichtbar machen —
          eine Ein-Punkt-"Kurve" hat keinen Pfad. */}
      {agentSegs.filter(s => s.length === 1).map((seg, i) => (
        <circle key={`agent-dot-${i}`} cx={seg[0].x} cy={seg[0].y} r={3} fill={color} fillOpacity={0.8} />
      ))}

      {datenpunkte.map((p, i) => {
        const av = p.agent[agentKey];
        if (av == null) return null;
        const dim = p.istAbwesend || p.istKurzeWoche;
        return (
          <g key={`mark-${i}`}>
            {dim && (
              <line x1={toX(i)} y1={PT} x2={toX(i)} y2={PT + cH}
                stroke={p.istAbwesend ? '#f59e0b' : '#9ca3af'}
                strokeWidth={1} strokeDasharray="2 3" strokeOpacity={0.5} />
            )}
            <circle cx={toX(i)} cy={toY(av)} r={dim ? 4 : 0}
              fill={p.istAbwesend ? '#f59e0b' : color} fillOpacity={dim ? 0.5 : 0.6} />
          </g>
        );
      })}

      {/* Maus-Tracker: folgt der Mausposition, snapt auf die naechste KW,
          zeigt Datumsbereich + Werte — statt fixer Hotspots pro Punkt. */}
      {hp && (
        <g pointerEvents="none">
          <line x1={toX(hoverIdx)} y1={PT} x2={toX(hoverIdx)} y2={PT + cH}
            stroke="var(--text-secondary,#6b7280)" strokeWidth={1} strokeOpacity={0.6} />
          {hp.agent[agentKey] != null && (
            <circle cx={toX(hoverIdx)} cy={toY(hp.agent[agentKey])} r={4} fill={color} />
          )}
          {(() => {
            const av = hp.agent[agentKey], tv = hp.team[teamKey];
            const rows = [
              { text: `MA: ${av != null ? formatTip(av) : '–'}`, fill: color },
              ...(tv != null ? [{ text: `Ø Team: ${formatTip(tv)}`, fill: 'var(--text-tertiary,#9ca3af)' }] : []),
              ...(getZielwert ? [{ text: `Ziel: ${formatTip(getZielwert(hp))}`, fill: '#c08a1e' }] : []),
            ];
            const boxW = 132, headerH = 26, rowH = 11, boxH = headerH + rows.length * rowH + 6;
            const anchorX = Math.min(Math.max(toX(hoverIdx), PL + boxW / 2 + 2), W - PR - boxW / 2 - 2);
            const boxX = anchorX - boxW / 2;
            const boxY = PT + 2;
            return (
              <g>
                <rect x={boxX} y={boxY} width={boxW} height={boxH} rx={5}
                  fill="var(--bg-card,#1d242e)" fillOpacity={0.82}
                  stroke="var(--border,#313a47)" strokeOpacity={0.9} strokeWidth={0.8} />
                <text x={boxX + 8} y={boxY + 14} fontSize={10} fontWeight={600} fill="var(--text-primary,#e5e7eb)">
                  {hp.labelFull || hp.label}
                </text>
                <text x={boxX + 8} y={boxY + 24} fontSize={9} fill="var(--text-tertiary,#9ca3af)">
                  {fmtDate(hp.from)}–{fmtDate(hp.to)}
                </text>
                {rows.map((r, ri) => (
                  <text key={ri} x={boxX + 8} y={boxY + headerH + (ri + 1) * rowH} fontSize={9} fill={r.fill}>
                    {r.text}
                  </text>
                ))}
              </g>
            );
          })()}
        </g>
      )}
    </svg>
  );
}

function TabBtn({ label, active, color, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: 12, padding: '5px 14px', borderRadius: 6, cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: active ? 500 : 400, border: active ? `1.5px solid ${color}` : '0.5px solid var(--border)',
        background: active ? color : 'transparent',
        color: active ? '#fff' : disabled ? 'var(--text-tertiary,#aaa)' : 'var(--text-secondary)',
        opacity: disabled ? 0.5 : 1, transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
}

export default function AgentTrendChart({ data, loading }) {
  const [activeTab, setActiveTab] = useState('abg');

  if (loading) {
    return <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)' }}>Leistungsverlauf wird geladen…</div>;
  }
  if (!data || !data.datenpunkte || data.datenpunkte.length === 0) return null;

  const { datenpunkte, granularitaet, teamSize, hasFCRData } = data;
  const hasTeam = teamSize > 1;

  const formatMin = v => {
    if (v == null) return '–';
    const h = Math.floor(v / 60), m = Math.round(v % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // Zielwerte (Unternehmensvorgabe) — feste Benchmark statt schwankendem
  // Team-Schnitt. "Abgeschlossen" ist pro Arbeitstag definiert, daher mit
  // sollTage der jeweiligen Woche skaliert (Kurzwochen bekommen ein
  // proportional niedrigeres Ziel statt eines unerreichbaren Fixwerts).
  const tabs = [
    // "Abgeschlossen" pro aktivem Arbeitstag — fair bei Teilzeit/Ferien/Krank
    // (Ziel 20/Tag fuer alle gleich, statt einem 100/Woche das Teilzeit benachteiligt).
    { key: 'abg',   label: 'Abgeschlossen',  color: '#185FA5',  disabled: false,
      agentKey: 'geloestProTag', teamKey: 'avgGeloestProTag',
      formatY: v => Math.round(v), formatTip: v => `${v} /Tag`,
      getZielwert: () => 20 },
    { key: 'bearb', label: 'Bearbeitungszeit', color: '#0e9f6e', disabled: false,
      agentKey: 'avgMin',    teamKey: 'avgMin',     formatY: v => formatMin(v),  formatTip: v => formatMin(v),
      getZielwert: () => 15 },
    { key: 'fcr',   label: 'FCR-Rate',         color: '#7c3aed', disabled: !hasFCRData,
      agentKey: 'fcr',       teamKey: 'avgFcr',     formatY: v => `${Math.round(v)}%`, formatTip: v => `${Math.round(v)}%`,
      getZielwert: () => 60, clampMax: 100 },
  ];

  const current = tabs.find(t => t.key === activeTab) || tabs[0];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {tabs.map(t => (
            <TabBtn key={t.key} label={t.label} active={activeTab === t.key} color={t.color}
              disabled={t.disabled} onClick={() => !t.disabled && setActiveTab(t.key)} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, fontSize: 11, flexWrap: 'wrap' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary)' }}>
            <svg width={24} height={8}><line x1={0} y1={4} x2={24} y2={4} stroke={current.color} strokeWidth={2} strokeLinecap="round"/></svg>
            Mitarbeiter
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary)' }}>
            <svg width={24} height={8}><line x1={0} y1={4} x2={24} y2={4} stroke="#c08a1e" strokeWidth={2} strokeDasharray="1 3" strokeLinecap="round"/></svg>
            Zielwert
          </span>
          {hasTeam && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-tertiary,#9ca3af)' }}>
              <svg width={24} height={8}><line x1={0} y1={4} x2={24} y2={4} stroke="var(--text-tertiary,#9ca3af)" strokeWidth={1.5} strokeOpacity={0.45} strokeDasharray="4 4" strokeLinecap="round"/></svg>
              Ø Team ({teamSize} MA)
            </span>
          )}
          {granularitaet === 'weekly' && (
            <>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary)' }}>
                <svg width={10} height={14}><line x1={5} y1={0} x2={5} y2={14} stroke="#f59e0b" strokeWidth={1} strokeDasharray="2 2"/></svg>
                Abwesenheit / Kurzwoche
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary)' }}>
                <svg width={24} height={8}><line x1={0} y1={4} x2={24} y2={4} stroke="#f59e0b" strokeWidth={1.75} strokeOpacity={0.55} strokeDasharray="5 4" strokeLinecap="round"/></svg>
                Keine Daten (Ferien/abwesend)
              </span>
            </>
          )}
        </div>
      </div>

      <Chart
        datenpunkte={datenpunkte}
        agentKey={current.agentKey}
        teamKey={current.teamKey}
        color={current.color}
        formatY={current.formatY}
        formatTip={current.formatTip}
        getZielwert={current.getZielwert}
        clampMax={current.clampMax}
      />

      <div style={{ fontSize: 11, color: 'var(--text-tertiary,#aaa)', marginTop: 6 }}>
        {granularitaet === 'weekly'
          ? 'nur vollständig abgeschlossene Wochen'
          : 'monatliche Daten — wöchentlich ab nächstem Wochenende'}
      </div>
    </div>
  );
}
